import Box from "@mui/material/Box";

import type { ChecklistItem } from "@/libs/tournamentSetupApi";

import { TermHelp } from "../setup/term-help";
import { DenseButton, NewBadge, Pill, SetupCard, tone } from "../setup/ui";

const GROUPS: { key: ChecklistItem["group"]; label: string }[] = [
  { key: "required", label: "TO OPEN REGISTRATION" },
  { key: "recommended", label: "RECOMMENDED BEFORE YOU OPEN" },
  { key: "optional", label: "OPTIONAL, ADD ANY TIME" },
];

function Mark({ item }: { item: ChecklistItem }) {
  const done = item.state === "done";
  const required = item.group !== "optional";
  return (
    <Box
      aria-label={done ? "Done" : item.state === "warning" ? "Needs attention" : required ? "To do" : "Optional"}
      component="span"
      role="img"
      sx={{
        width: 18,
        height: 18,
        borderRadius: "50%",
        flex: "none",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
        ...(done
          ? { bgcolor: tone.brandSoft, color: tone.brandText }
          : item.state === "warning"
            ? { border: `1.5px solid ${tone.badBar}` }
            : required
              ? { border: `1.5px solid ${tone.warnBar}` }
              : { border: `1.5px dashed ${tone.faintLine}` }),
      }}
    >
      {done ? (
        <svg aria-hidden fill="none" height="11" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3.5" viewBox="0 0 24 24" width="11">
          <path d="M5 12l5 5L20 7" />
        </svg>
      ) : null}
    </Box>
  );
}

/** The Overview checklist from the server: required to open, recommended,
 * optional, and the "later" timeline (F3). */
export function Checklist({
  items,
  doneCount,
  total,
  laterLine,
  onAction,
}: {
  items: ChecklistItem[];
  doneCount: number;
  total: number;
  laterLine: string;
  onAction: (item: ChecklistItem) => void;
}) {
  return (
    <SetupCard extra="Only the first group is needed to open registration." sx={{ overflow: "hidden" }} title="Checklist">
      {GROUPS.map((group) => {
        const rows = items.filter((item) => item.group === group.key);
        if (!rows.length) return null;
        const two = group.key !== "optional";
        return (
          <Box key={group.key}>
            <Box sx={{ fontSize: 11, fontWeight: 700, letterSpacing: ".3px", color: tone.subtle, p: "8px 14px 4px", display: "flex", alignItems: "center", gap: 1 }}>
              <span>{group.label}</span>
              {group.key === "required" ? (
                <Pill variant={doneCount === total ? "green" : "amber"}>
                  {doneCount} of {total} done
                </Pill>
              ) : null}
            </Box>
            {rows.map((item) => (
              <Box key={item.key} sx={{ display: "flex", alignItems: "center", gap: "10px", px: "14px", borderTop: `1px solid ${tone.divider}`, minHeight: two ? 44 : 34, py: two ? "4px" : 0 }}>
                <Mark item={item} />
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 12.5,
                    color: tone.subtle,
                    ...(two ? { lineHeight: 1.35 } : { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }),
                  }}
                >
                  <Box component="b" sx={{ fontSize: 13, fontWeight: 600, color: tone.ink, display: two ? "flex" : "inline", alignItems: "center", gap: "6px" }}>
                    {item.title}
                    {item.key === "official" ? <TermHelp term="official_event" /> : null}
                    {item.key === "refunds" ? <NewBadge /> : null}
                    {item.badge ? <Pill variant="amber">{item.badge}</Pill> : null}
                  </Box>
                  {two ? item.summary : ` · ${item.summary}`}
                  {item.warning ? (
                    <Box component="span" sx={{ display: "block", color: tone.badText }}>
                      {item.warning}
                    </Box>
                  ) : null}
                </Box>
                <DenseButton aria-label={`${item.action_label} ${item.title.toLowerCase()}`} onClick={() => onAction(item)} size="sm" sx={{ height: { xs: 44, md: 26 }, minWidth: { xs: 44, md: 0 }, fontSize: 12 }}>
                  {item.action_label}
                </DenseButton>
              </Box>
            ))}
          </Box>
        );
      })}
      <Box sx={{ display: "flex", alignItems: "center", px: "14px", minHeight: 34, borderTop: `1px solid ${tone.divider}`, bgcolor: tone.page, fontSize: 12.5, color: tone.subtle }}>
        <span>
          <Box component="b" sx={{ color: tone.ink, fontWeight: 600 }}>
            Later
          </Box>{" "}
          · {laterLine}
        </span>
      </Box>
    </SetupCard>
  );
}
